import type { MusicGraphicMode, MusicalToken, RhythmicPatternPayload, RhythmReadingMethod } from '../../core/types/music';
import { normalizeRhythmPattern, normalizeTokenArray } from '../../core/music/tokens';
import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type { SequencerPayload, SequencerRuntime } from '../../core/types/payloads/sequencer.types';
import { applyStimulusFields } from '../../core/print/blockStimulus';
import { sanitizeSequenceItems } from '../../core/text/sanitizeSequenceItems';
import type { PluginContext } from '../../core/types/plugin.base.types';
import type { Participant, RoutingMode } from '../../core/types/room.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';

function shuffleOrder(n: number): number[] {
  const order = Array.from({ length: n }, (_, i) => i);
  for (let i = order.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  if (n > 1 && order.every((v, i) => v === i)) {
    [order[0], order[1]] = [order[1], order[0]];
  }
  return order;
}

function isCorrectOrder(order: number[], correct: number[]): boolean {
  if (order.length !== correct.length) return false;
  return order.every((v, i) => v === correct[i]);
}

function rebuildAggregates(
  responses: SequencerRuntime['responses'],
  finalized: SequencerRuntime['finalized'],
  correctOrder: number[],
): SequencerRuntime['aggregates'] {
  const submittedIds = Object.keys(finalized).filter((id) => finalized[id]);
  const submitted = submittedIds.length;
  const correct = submittedIds.filter((id) => isCorrectOrder(responses[id] ?? [], correctOrder)).length;
  return {
    submitted,
    correct,
    inProgress: Math.max(0, Object.keys(responses).length - submitted),
  };
}

export function validateSequencerPayload(raw: unknown): SequencerPayload {
  if (!raw || typeof raw !== 'object') throw new Error('Sequencer payload must be an object');
  const data = raw as Partial<SequencerPayload>;
  if (!data.title) throw new Error('Sequencer requires title');
  const items = sanitizeSequenceItems(data.items);
  if (items.length < 2) {
    throw new Error('Sequencer requires at least 2 items');
  }
  const n = items.length;
  let correctOrder = Array.isArray(data.correctOrder)
    ? data.correctOrder.map(Number)
    : Array.from({ length: n }, (_, i) => i);
  if (correctOrder.length !== n || new Set(correctOrder).size !== n) {
    correctOrder = Array.from({ length: n }, (_, i) => i);
  }
  const printLayout = data.printLayout === 'rhythm-bar' ? 'rhythm-bar' : undefined;
  const meter = data.meter ? String(data.meter).trim() : undefined;
  const rhythmCells = Array.isArray(data.rhythmCells)
    ? data.rhythmCells.map((cell, i) => ({
        syllable: String(cell?.syllable || items[i] || '').trim(),
        figure:
          cell?.figure === 'eighth-pair' || cell?.figure === 'sixteenth-quad'
            ? cell.figure
            : ('quarter' as const),
      }))
    : undefined;

  const rhythmPattern = normalizeRhythmPattern(data.rhythmPattern);
  const patternTokens =
    rhythmPattern?.pattern ??
    (Array.isArray(data.patternTokens) ? normalizeTokenArray(data.patternTokens) : undefined);
  const rhythmMethod =
    data.rhythmMethod === 'takadimi' || data.rhythmMethod === 'kodaly'
      ? data.rhythmMethod
      : data.rhythmMethod === 'tradicional'
        ? 'tradicional'
        : undefined;
  const graphicMode =
    data.graphicMode === 'symbols-labels' ? 'symbols-labels' : data.graphicMode === 'symbols' ? 'symbols' : undefined;

  return applyStimulusFields(
    {
      title: String(data.title),
      prompt: String(data.prompt || 'Ordena los elementos en el orden correcto'),
      items,
      correctOrder,
      ...(printLayout ? { printLayout } : {}),
      ...(meter ? { meter } : {}),
      ...(rhythmCells?.length ? { rhythmCells } : {}),
      ...(patternTokens?.length ? { patternTokens } : {}),
      ...(rhythmPattern ? { rhythmPattern } : {}),
      ...(rhythmMethod ? { rhythmMethod } : {}),
      ...(graphicMode ? { graphicMode } : {}),
    },
    raw,
    'order',
  );
}

export function createInitialSequencerRuntime(
  payload: SequencerPayload,
  _routing: RoutingMode,
): SequencerRuntime {
  return {
    responses: {},
    finalized: {},
    aggregates: { submitted: 0, correct: 0, inProgress: 0 },
    revealed: false,
  };
}

export function initialStudentOrder(payload: SequencerPayload): number[] {
  return shuffleOrder(payload.items.length);
}

export const sequencerLogic: ActivityLogicPlugin<'order'> = {
  mode: 'order',

  validatePayload: validateSequencerPayload,
  createInitialRuntime: createInitialSequencerRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope, participant: Participant) {
    if (input.action === 'INIT_ORDER') {
      const order = initialStudentOrder(ctx.payload);
      const responses = {
        ...ctx.runtime.responses,
        [participant.participantId]: order,
      };
      return {
        runtime: {
          ...ctx.runtime,
          responses,
          aggregates: rebuildAggregates(responses, ctx.runtime.finalized, ctx.payload.correctOrder),
        },
      };
    }

    if (input.action === 'REORDER') {
      if (ctx.phase !== 'ACTIVE') {
        return { runtime: ctx.runtime, rejected: true, rejectReason: 'NOT_ACTIVE' };
      }
      const data = input.data as { order?: number[] } | undefined;
      if (!Array.isArray(data?.order)) {
        return { runtime: ctx.runtime, rejected: true, rejectReason: 'INVALID_ORDER' };
      }
      const n = ctx.payload.items.length;
      const order = data.order.map(Number);
      if (order.length !== n || new Set(order).size !== n) {
        return { runtime: ctx.runtime, rejected: true, rejectReason: 'INVALID_ORDER' };
      }
      const responses = {
        ...ctx.runtime.responses,
        [participant.participantId]: order,
      };
      return {
        runtime: {
          ...ctx.runtime,
          responses,
          aggregates: rebuildAggregates(responses, ctx.runtime.finalized, ctx.payload.correctOrder),
        },
      };
    }

    if (input.action === 'SUBMIT') {
      if (ctx.phase !== 'ACTIVE') {
        return { runtime: ctx.runtime, rejected: true, rejectReason: 'NOT_ACTIVE' };
      }
      const order = ctx.runtime.responses[participant.participantId];
      if (!order?.length) {
        return { runtime: ctx.runtime, rejected: true, rejectReason: 'NO_ORDER' };
      }
      const responses = { ...ctx.runtime.responses, [participant.participantId]: [...order] };
      const finalized = { ...ctx.runtime.finalized, [participant.participantId]: true };
      return {
        runtime: {
          ...ctx.runtime,
          responses,
          finalized,
          aggregates: rebuildAggregates(responses, finalized, ctx.payload.correctOrder),
        },
      };
    }

    return { runtime: ctx.runtime };
  },

  onHostAction(ctx, action: HostActionPayload) {
    if (action.action === 'REVEAL') {
      return {
        runtime: {
          ...ctx.runtime,
          revealed: true,
          aggregates: rebuildAggregates(ctx.runtime.responses, ctx.runtime.finalized, ctx.payload.correctOrder),
        },
      };
    }
    return { runtime: ctx.runtime };
  },
};

export function demoSequencerPayload(): SequencerPayload {
  // Neutro por asignatura — NUNCA hitos de Historia (fuga a Música vía defaultPayloadForMode).
  return {
    title: 'Secuencia de aprendizaje',
    prompt: 'Ordena los pasos en el orden correcto',
    items: ['Paso inicial', 'Exploración', 'Práctica', 'Cierre'],
    correctOrder: [0, 1, 2, 3],
  };
}

export function demoEstructuraMusicalPayload(): SequencerPayload {
  const items = ['Intro', 'Estrofa', 'Coro', 'Solo instrumental', 'Outro'];
  return {
    title: 'Ordenador de Estructura',
    prompt: 'Ordena los bloques estructurales de la canción',
    items,
    correctOrder: items.map((_, i) => i),
  };
}
